import React, { useContext, useEffect, useMemo, useRef } from 'react';
import icons from '@bbkAdminUtils/icons';
import {
  PlayContext,
  PlayObjectProperty,
  PlayValidationErrorType,
} from '@bbkAdminComponents/play/play-context';
import Popover from '@mui/material/Popover';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import { OfferPresentationCard } from '@bbkAdminComponents/experiences/offers/new-offer-flow-2022/choose-category';
import { cs } from '@bbkAdminUtils/css-utils';
import _ from 'lodash';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { DisableContainer } from '@bbkAdminComponents/play/disable-container';
import { PlayPresentation } from '@bbkAdminRedux/rtkq/play.slice';

enum PresentationDropdownStep {
  NONE = 'none',
  SHOW_OPTIONS = 'showOptions',
  SHOW_CONFIRMATION = 'showConfirmation',
}

const offerModeOptions = [
  {
    title: 'Modal',
    helpText: 'Show an offer that blocks out the rest of the screen.',
    icon: icons.modal,
    type: PlayPresentation.MODAL,
    dropdownIcon: icons.modalSmall,
  },
  {
    title: 'Pop-up',
    helpText: 'Show an offer that doesn’t block out the rest of the screen.',
    icon: icons.popup,
    type: PlayPresentation.POP_UP,
    dropdownIcon: icons.popupSmall,
  },
  {
    title: 'Banner',
    helpText: 'Show an offer that appears above or below the screen’s content.',
    icon: icons.banner,
    type: PlayPresentation.BANNER,
    dropdownIcon: icons.bannerSmall,
  },
  {
    title: 'Offer page',
    helpText:
      'Show a pack of three offers that is rendered in a separate page.',
    icon: icons.offerCardPack,
    type: PlayPresentation.CARD,
    dropdownIcon: icons.offerCardPackSmall,
  },
];

export const OfferPresentationModeDropdown = () => {
  const anchorRef = useRef(null);
  const {
    setPlayObjectProperty,
    getPlayObjectProperty,
    resetOfferSelection,
    resetPresentationSelection,
    isPresentationAllowedForTriggerType,
    playValidationErrors,
  } = useContext(PlayContext);
  const offerNodeType = getPlayObjectProperty(
    PlayObjectProperty.OFFER_NODE_TYPE
  );
  const offerPresentationMode = getPlayObjectProperty(
    PlayObjectProperty.PRESENTATION
  ) as PlayPresentation;

  const offerModes = useMemo(() => {
    return offerModeOptions.filter((offerMode) =>
      isPresentationAllowedForTriggerType(offerMode.type)
    );
  }, [isPresentationAllowedForTriggerType]);

  const [step, setStep] = React.useState<{
    type: PresentationDropdownStep;
    value?: PlayPresentation;
  }>({ type: PresentationDropdownStep.NONE });

  const handleClose = () => {
    setStep({ type: PresentationDropdownStep.NONE });
  };

  const optionOpen =
    step.type === PresentationDropdownStep.SHOW_OPTIONS &&
    offerModes.length > 1;
  const confirmationOpen =
    step.type === PresentationDropdownStep.SHOW_CONFIRMATION;
  const id = optionOpen || confirmationOpen ? 'simple-popover' : undefined;

  const presentationValue = offerModes.find(
    (mode) => mode.type === offerPresentationMode
  );

  useEffect(() => {
    //check if existing option is not in list
    if (!presentationValue) {
      BlackToastTrigger({
        content: 'Selections have been reset as targeting type has changed.',
      });
      resetPresentationSelection();
      if (offerNodeType) {
        resetOfferSelection(offerNodeType);
      }
    }
  }, [presentationValue]);

  const handlePresentationChange = (
    val: PlayPresentation | undefined = undefined
  ) => {
    setPlayObjectProperty(PlayObjectProperty.PRESENTATION, step.value ?? val);
    if (offerNodeType) {
      resetOfferSelection(offerNodeType);
    }
    setStep({ type: PresentationDropdownStep.NONE });
  };

  return (
    <DisableContainer>
      <div className="play-offer-presentation-dropdown">
        <button
          aria-describedby={id}
          ref={anchorRef}
          onClick={() =>
            setStep({ type: PresentationDropdownStep.SHOW_OPTIONS })
          }
          className={cs(
            'tw-flex tw-items-center tw-rounded-md !tw-border tw-border-solid tw-border-[#DDDFE2] tw-px-4 tw-py-2',
            {
              'tw-border-[#295E7D]': optionOpen,
            }
          )}
        >
          <span className="tw-mr-2">{presentationValue?.dropdownIcon}</span>
          <span>{presentationValue?.title}</span>
          {offerModes.length > 1 && (
            <span
              className={cs('tw-ml-5 tw-transition tw-duration-300', {
                'tw-rotate-180': optionOpen,
              })}
            >
              {icons.chevronDown}
            </span>
          )}
        </button>
        <Popover
          id={id}
          open={optionOpen}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <div className="tw-flex tw-max-w-[24rem] tw-flex-wrap tw-justify-evenly tw-py-6">
            {offerModes.map((mode) => (
              <div className="tw-mb-5" key={mode.type}>
                <OfferPresentationCard
                  title={mode.title}
                  helpText={mode.helpText}
                  icon={mode.icon}
                  onClick={() => {
                    const offerValidationError = _.get(playValidationErrors, [
                      PlayValidationErrorType.Offer,
                    ]);
                    if (offerValidationError?.hasValidOffer) {
                      setStep({
                        type: PresentationDropdownStep.SHOW_CONFIRMATION,
                        value: mode.type,
                      });
                    } else {
                      handlePresentationChange(mode.type);
                    }
                  }}
                />
              </div>
            ))}
          </div>
        </Popover>
        <Popover
          id={id}
          open={confirmationOpen}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <div className="tw-max-w-80 tw-p-4">
            <div className="tw-mb-4 tw-flex tw-items-center">
              <span className="warning-icon tw-mr-2">{icons.warining1}</span>
              <span className="tw-font-bold">Confirm widget switch</span>
            </div>
            <span>
              If you switch the widget, you will have to re-select offers.
            </span>
            <div className="tw-flex tw-items-center tw-pt-4">
              <ButtonV2 primary onClick={() => handlePresentationChange()}>
                Confirm switch
              </ButtonV2>
              <ButtonV2
                secondary
                className="tw-ml-5"
                onClick={() => {
                  setStep({ type: PresentationDropdownStep.NONE });
                }}
              >
                Cancel
              </ButtonV2>
            </div>
          </div>
        </Popover>
      </div>
    </DisableContainer>
  );
};

const OfferPresentationMode = () => {
  const { setPlayObjectProperty, isPresentationAllowedForTriggerType } =
    useContext(PlayContext);

  return (
    <div className="tw-mt-4 tw-min-w-[45rem] tw-rounded-sm tw-border tw-border-[#5CB5E5] tw-bg-white tw-px-6 tw-py-3">
      <span>Choose a widget.</span>
      <div className="tw-flex tw-pt-5">
        {offerModeOptions
          .filter((offerMode) =>
            isPresentationAllowedForTriggerType(offerMode.type)
          )
          .map((mode) => (
            <OfferPresentationCard
              title={mode.title}
              helpText={mode.helpText}
              icon={mode.icon}
              onClick={() => {
                setPlayObjectProperty(
                  PlayObjectProperty.PRESENTATION,
                  mode.type
                );
              }}
              key={mode.title}
            />
          ))}
      </div>
    </div>
  );
};

export default OfferPresentationMode;
